import React, { useCallback, useMemo, useRef, useState } from 'react';

import { withStyles, WithStyles } from '@material-ui/core';
import _ from 'lodash';
import moment from 'moment-timezone';
import { ELumaBotFlowType, TAvailability, useAvailabilityUpdateOneBook } from 'ui-model';

import { useCurrentLanguage, useCurrentUser } from '@lib/hooks';
import { getTranslation } from '@lib/services/patientFormsService';

import { Button, Typography } from 'components/v2';

import { LumaBotContextProps, useLumaBotContext } from '../../LumaBot.context';
import useLumaBotSettings from '../../useLumaBotSettings';
import { EMessageType } from '../../WIP/ui-model/types';
import ExtendedScheduler from '../ExtendedScheduler';
import Message, { MessageProps } from '../Message';
import { TMessageWrapper } from '../MessageWrapper';

import styles from './MessageScheduler.styles';
import useCheckForAvailabilities from './useCheckForAvailabilities';

// how many characters users can read per millisecond:
const MILLISECONDS_MULTIPLIER = 20;

export interface MessageSchedulerProps extends Omit<MessageProps, 'classes'>, WithStyles<typeof styles> {
  message: TMessageWrapper;
  messageTemplate: string;
  onSubmit: LumaBotContextProps['pushAnswer'];
}

const MessageScheduler: React.FC<MessageSchedulerProps> = ({
  classes,
  message,
  messageTemplate,
  onSubmit,
  ...rest
}) => {
  const {
    currentFlowId,
    currentFlowTemplateId,
    mode,
    setMode,
    pushFlowTemplate,
    setCurrentAvailability,
  } = useLumaBotContext();
  const language = useCurrentLanguage();
  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id || '';
  const $isWidgetUser = currentUser?.$isWidgetUser;
  const schedulerItem = _.get(message, 'metadata.item');

  const schedulerFilters = schedulerItem?.properties?.params;

  const { isHideProviderDetails } = useLumaBotSettings();
  const showFilters = _.get(schedulerItem, 'properties.showFilters') && !isHideProviderDetails;

  const filters = useMemo(() => {
    const facility = _.join(schedulerFilters?.facility, ',');
    const provider = _.compact(schedulerFilters?.provider) as string[];
    const appointmentType = _.join(schedulerFilters?.appointmentType, ',');
    const includeNullApptTypes = appointmentType ? !(appointmentType.length > 0) : true;
    return { facility, provider, appointmentType, includeNullApptTypes };
  }, [schedulerFilters]);

  const divRef = useRef<HTMLDivElement>(null);
  const [isSchedulerDisabled, setSchedulerDisabled] = useState(false);
  const availabilityUpdateOneBookMutation = useAvailabilityUpdateOneBook();

  const [providerFilter, setProviderFilter] = useState(filters.provider);

  const { isLoading: isAvailabilitiesCountLoading, shouldHideScheduler } = useCheckForAvailabilities(filters);

  const handleBookAppointment = useCallback(
    (availability: TAvailability) => {
      if (!availability) return;

      availabilityUpdateOneBookMutation.mutate(
        {
          _id: availability._id,
          filters: availability.filters,
          lumabotFlow: currentFlowId,
          lumabotFlowTemplate: currentFlowTemplateId,
          patient: $isWidgetUser ? '' : currentUserId,
          source: availability.source,
        },
        {
          onSettled: () => {
            setSchedulerDisabled(true);
          },
          onSuccess: async (availabilityBooked) => {
            const availabilitySelectedLabel = `${moment(availability!.date).format('MMM D, YYYY - LT')}\n${
              availability!.provider.name
            }`;

            const isAppointmentTypeNull = !availability.appointmentType;

            const customPendingDelay = availabilitySelectedLabel.length * MILLISECONDS_MULTIPLIER;
            const availabilityDetails = {
              id: availabilityBooked._id,
              status: availabilityBooked.status,
              date: availabilityBooked.date,
              facility: availability.facility,
              provider: availability.provider,
              ...(isAppointmentTypeNull
                ? {}
                : {
                    appointmentType: {
                      name: availability.appointmentType?.name,
                      modality: availability.appointmentType?.modality,
                    },
                  }),
            };

            setCurrentAvailability(availabilityDetails);
            onSubmit(
              {
                text: availabilitySelectedLabel,
                type: EMessageType.Outbound,
                details: {
                  availability: availabilityDetails,
                },
              },
              { customDelay: customPendingDelay },
            );

            // TODO: would be better to have a `pushMessagesList` function to handle awaiting times between multiple submits like this
            await new Promise((resolve) => setTimeout(resolve, customPendingDelay));
          },
          onError: () => {
            pushFlowTemplate(ELumaBotFlowType.Error);
          },
        },
      );
    },
    [
      availabilityUpdateOneBookMutation,
      currentFlowId,
      currentFlowTemplateId,
      $isWidgetUser,
      currentUserId,
      setCurrentAvailability,
      onSubmit,
      pushFlowTemplate,
    ],
  );

  if (isAvailabilitiesCountLoading) return <Message message={message} {...rest} variant="typing" />;

  if (shouldHideScheduler) return null;

  return (
    <>
      <Message message={message} {...rest}>
        {messageTemplate && <Typography dangerouslySetInnerHTML={{ __html: messageTemplate }} />}
        <Button
          className={classes.schedulerButton}
          disabled={isSchedulerDisabled || _.get(message, 'metadata.nextMessage')}
          onClick={() => setMode('blocked')}
          variant="outlined"
          extraPadding
          extraRounded
          fullWidth
        >
          {/* @ts-ignore */}
          {getTranslation(schedulerItem, language, 'buttonLabel')}
        </Button>
        <div ref={divRef} />
      </Message>
      {mode === 'blocked' && (
        <ExtendedScheduler.Container>
          {showFilters && (
            <ExtendedScheduler.ProviderFilterPanel
              onChange={(filteredProviders) => {
                setProviderFilter(filteredProviders);
              }}
              preFilteredProviders={filters.provider}
              value={providerFilter}
            />
          )}
          <ExtendedScheduler.AvailabilitiesList
            filtersParams={{
              ...filters,
              provider: _.isEmpty(providerFilter) ? filters.provider.join(',') : providerFilter.join(','),
            }}
            onChange={(value) => {
              setMode('default');
              setTimeout(() => {
                handleBookAppointment(value);
              });
            }}
          />
        </ExtendedScheduler.Container>
      )}
    </>
  );
};

export default withStyles(styles, { name: 'MessageScheduler' })(MessageScheduler);
